import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Container, Box, CircularProgress, Typography, Button } from '@mui/material'
import { Helmet } from 'react-helmet-async'
import ApiService from '../services/ApiService'
import { connectSocket, socketEventEmitter, subscribeToCopyJob } from '../socket'

const EmailVerification = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState('verifying') // verifying, cloning, done, error
  const [error, setError] = useState(null)
  const socketConnectedRef = useRef(false)

  useEffect(() => {
    let isMounted = true

    const verifyEmail = async () => {
      try {
        // Step 1: Verify email and get copyJob info
        const data = await ApiService.get(`/auth/verify-email/${token}`)
        
        if (!isMounted) return

        if (data.error) {
          setStatus('error')
          setError(data.error)
          return
        }

        const { userId, copyJob } = data

        // Step 2: Check copyJob status
        if (!copyJob) {
          // No copyJob means cloning not needed, redirect immediately
          console.log('No copyJob, redirecting to login')
          navigate('/login')
          return
        }

        // If job is already done, redirect immediately
        if (copyJob.status === 'done') {
          console.log('CopyJob already done, redirecting to login')
          navigate('/login')
          return
        }

        // If job failed, show error but allow to proceed
        if (copyJob.status === 'failed') {
          console.log('CopyJob failed, redirecting to login anyway')
          navigate('/login')
          return
        }

        // Step 3: Job is queued or running - connect to socket and wait
        setStatus('cloning')
        
        if (!socketConnectedRef.current) {
          socketConnectedRef.current = true
          const serverUrl = process.env.REACT_APP_SERVER_URL
          connectSocket(serverUrl, userId, { copyJobId: copyJob.id })
          
          // Also subscribe explicitly in case connection was already established
          subscribeToCopyJob(copyJob.id)
          
          // Listen for copyJob updates
          const handleJobUpdate = (update) => {
            if (!isMounted) return
            if (update.jobId !== copyJob.id) return
            
            console.log('CopyJob update:', update)
            
            // Redirect when done or failed
            if (update.status === 'done' || update.status === 'failed') {
              setTimeout(() => navigate('/login'), 500)
            }
          }
          
          socketEventEmitter.on('copyJobUpdate', handleJobUpdate)
        }
      } catch (err) {
        if (!isMounted) return
        console.error('Error verifying email:', err)
        setStatus('error')
        setError(err?.response?.data?.error || err?.message || 'Verification failed')
      }
    }

    verifyEmail()

    return () => {
      isMounted = false
      socketEventEmitter.removeAllListeners('copyJobUpdate')
    }
  }, [navigate, token])

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Verifying your email
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait...
            </Typography>
          </>
        )
      case 'cloning':
        return (
          <>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Setting up your account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We&apos;re preparing sample data for your account. This may take up to 2 minutes — please keep this tab open.
            </Typography>
          </>
        )
      case 'done':
        return (
          <>
            <Typography variant="h6" gutterBottom color="success.main">
              Account Ready!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Redirecting to login...
            </Typography>
          </>
        )
      case 'error':
        return (
          <>
            <Typography variant="h6" gutterBottom color="error">
              Verification Failed
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button variant="contained" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </>
        )
      default:
        return null
    }
  }

  return (
    <Container
      maxWidth="xs"
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}
    >
      <Helmet>
        <title>Treegress - Email verification</title>
      </Helmet>
      <Box
        sx={{
          border: '1px solid #C4C4C4',
          borderRadius: '8px',
          padding: '32px',
          textAlign: 'center'
        }}
      >
        {renderContent()}
      </Box>
    </Container>
  )
}

export default EmailVerification